import { useMutation } from '@tanstack/react-query';
import { LoginRequest, LoginResponse } from '../types/auth';
import apiService from './apiService';

// Custom error to preserve API response (remainingAttempts, accountSuspended)
export class LoginError extends Error {
  constructor(
    message: string,
    public remainingAttempts?: number,
    public accountSuspended?: boolean
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

// Login function
export async function login(credentials: Omit<LoginRequest, 'platform' | 'ipADD'>): Promise<LoginResponse> {
  try {
    const payload = await apiService.addCommonData(credentials);

    const response = await apiService.postRaw<LoginResponse & { remainingAttempts?: number; accountSuspended?: boolean }>('/api/Users/login', payload);
    if (response.status === 1) {
      const msg = response.message?.warning || response.message?.success || 'Unknown error';
      throw new LoginError(msg, response.remainingAttempts, response.accountSuspended);
    }
    return response || { status: 0, message: { success: '', warning: '' } };
  } catch (error: any) {
    // Preserve remainingAttempts/accountSuspended from axios error response (4xx/5xx)
    if (error?.response?.data) {
      const data = error.response.data;
      const msg = data?.message?.warning || data?.message?.success || error?.message || 'Login failed';
      throw new LoginError(msg, data?.remainingAttempts, data?.accountSuspended);
    }
    if (error instanceof LoginError) throw error;
    throw error;
  }
}

// Google Auth function
export interface GoogleAuthRequest {
  idToken?: string;
  accessToken?: string;
  referralCode?: string;
}

export interface GoogleAuthResponse extends LoginResponse {
  isNewUser?: boolean;
}

export async function googleAuth(data: GoogleAuthRequest): Promise<GoogleAuthResponse> {
  try {
    const response = await apiService.postRaw<GoogleAuthResponse>(
      '/api/Users/google-auth',
      data,
      { timeout: 30000 }
    );
    if (response.status === 1) {
      throw new Error(response.message?.warning || 'Authentication failed');
    }
    return response;
  } catch (error) {
    throw error;
  }
}

// TanStack Query hooks
export function useLogin() {
  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      console.log('Login successful:', data);
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
}

export function useGoogleAuth() {
  return useMutation({
    mutationFn: googleAuth,
    onSuccess: (data) => {
    },
    onError: (error) => {
      console.error('Google auth failed:', error);
    },
  });
}

// Telegram Auth
export interface TelegramAuthData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
  referralCode?: string;
}

export interface TelegramAuthResponse extends LoginResponse {
  isNewUser?: boolean;
}

export async function telegramAuth(data: TelegramAuthData): Promise<TelegramAuthResponse> {
  try {
    const response = await apiService.postRaw<TelegramAuthResponse>(
      '/api/Users/telegram-auth',
      data,
      { timeout: 30000 }
    );
    if (response.status === 1) {
      throw new Error(response.message?.warning || 'Authentication failed');
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export function useTelegramAuth() {
  return useMutation({
    mutationFn: telegramAuth,
    onSuccess: (data) => {
      console.log('Telegram auth successful:', data);
    },
    onError: (error) => {
      console.error('Telegram auth failed:', error);
    },
  });
}

// Microsoft Auth
export interface MicrosoftAuthRequest {
  accessToken: string;
  referralCode?: string;
}

export interface MicrosoftAuthResponse extends LoginResponse {
  isNewUser?: boolean;
}

export async function microsoftAuth(data: MicrosoftAuthRequest): Promise<MicrosoftAuthResponse> {
  try {
    const response = await apiService.postRaw<MicrosoftAuthResponse>(
      '/api/Users/microsoft-auth',
      data,
      { timeout: 30000 }
    );
    if (response.status === 1) {
      throw new Error(response.message?.warning || 'Authentication failed');
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export function useMicrosoftAuth() {
  return useMutation({
    mutationFn: microsoftAuth,
    onSuccess: (data) => {
      console.log('Microsoft auth successful:', data);
    },
    onError: (error) => {
      console.error('Microsoft auth failed:', error);
    },
  });
}

// Utility function to get stored token
export const getStoredToken = (): string | null => {
  return apiService.getToken();
};

// Utility function to get stored user data
export const getStoredUserData = () => {
  return apiService.getStoredUserData();
};

export const getStoredAllUserData = () => {
  return apiService.getStoredAllUserData();
};

// Utility function to clear stored auth data
export const clearStoredAuthData = () => {
  apiService.clearAllData();
};

// Utility function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  return apiService.isAuthenticated();
}; 